/**
 * تب «فی تمام‌شده»: نخ + کارمزد بافت + کارمزد رنگرزی (+ حملِ انتخابی فقط در سرجمع).
 * تیکِ هر مرحله تعیین می‌کند در جمعِ کل و «فی هر کیلو»ی پایین بیاید یا نه.
 *
 * نکته: به Statistic نباید رشتهٔ fmtKg/fmtRial داد — آنtd با Number → NaN و سلول خالی می‌شود.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Tag, Checkbox, Segmented, Space, Input, Tooltip } from "antd";
import Alert from "../../components/GxAlert";
import { scReportCostPerKg } from "../../api/client";
import { JalaliDateInput } from "../../utils/dateUtils";
import { byStr, byNum } from "../../utils/sorters";
import { fmtKg, fmtRial } from "./scConstants";
import { PERIOD_PRESETS, periodRange } from "./scPeriods";
import GxTable from "../../components/GxTable";

const STAGE_DEFS = [
  { key: "yarn", label: "نخ (خرید درین)", color: "#1677ff" },
  { key: "weaving", label: "کارمزد بافت", color: "#722ed1" },
  { key: "dyeing", label: "کارمزد رنگرزی", color: "#eb2f96" },
  { key: "freight", label: "حمل (کرایه‌های با ما)", color: "#d46b08" },
];

const TABLE_SCROLL_X = 980;

export default function CostPerKgTab() {
  const [preset, setPreset] = useState("all");
  const [manualFrom, setManualFrom] = useState(null);
  const [manualTo, setManualTo] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [ticked, setTicked] = useState({ yarn: true, weaving: true, dyeing: true, freight: true });

  const range = useMemo(() => {
    if (manualFrom || manualTo) return { date_from: manualFrom, date_to: manualTo, label: "بازهٔ دستی" };
    return periodRange(preset);
  }, [preset, manualFrom, manualTo]);

  useEffect(() => {
    const params = {};
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    scReportCostPerKg(params).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range.date_from, range.date_to]);

  const stages = data?.stages || {};
  const finishedKg = data?.finished_kg || 0;

  const pickedTotal =
    (ticked.yarn ? stages.yarn?.amount || 0 : 0) +
    (ticked.weaving ? stages.weaving?.amount || 0 : 0) +
    (ticked.dyeing ? stages.dyeing?.amount || 0 : 0) +
    (ticked.freight ? stages.freight?.amount || 0 : 0);
  const pickedFi = finishedKg > 0 ? Math.round(pickedTotal / finishedKg) : null;

  const search = q.trim();
  const rows = (data?.by_fabric || []).filter((r) =>
    !search || `${r.fabric_type || ""} ${r.color || ""}`.includes(search));

  const cols = [
    { title: "نوع پارچه", dataIndex: "fabric_type", width: 140, ellipsis: true,
      sorter: byStr("fabric_type"), gxFilter: "enum", render: (v) => v || "—" },
    { title: "رنگ", dataIndex: "color", width: 100, ellipsis: true,
      sorter: byStr("color"), gxFilter: "enum", render: (v) => v || "—" },
    { title: "وزن (آماده)", dataIndex: "kg", width: 120, align: "center",
      sorter: byNum("kg"), defaultSortOrder: "descend", gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtKg(v)}</span> },
    { title: "سهم نخ / کیلو", dataIndex: "yarn_fi_share", width: 130, align: "center",
      sorter: byNum("yarn_fi_share"), gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
    { title: "فی بافت", dataIndex: "weaving_fi", width: 120, align: "center",
      sorter: byNum("weaving_fi"), gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
    { title: "فی رنگرزی", dataIndex: "dyeing_fi", width: 120, align: "center",
      sorter: byNum("dyeing_fi"), gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
    { title: "فی تمام‌شده", dataIndex: "cost_per_kg", width: 150, align: "center",
      sorter: byNum("cost_per_kg"), gxFilter: "num",
      render: (v, r) => (
        <div className="gx-cost-cell">
          <b className="gx-num" style={{ color: "#107C41" }}>{fmtRial(v)}</b>
          {r.estimated && (
            <Tooltip title="بخشی از فی از میانگینِ کل برآورد شده (کارمزدِ اختصاصیِ این مدل ثبت نشده)">
              <Tag color="orange" style={{ margin: 0 }}>تقریبی</Tag>
            </Tooltip>
          )}
        </div>
      ) },
    { title: "ارزش کل", dataIndex: "total_cost", width: 140, align: "center",
      sorter: byNum("total_cost"), gxFilter: "num",
      render: (v) => <span className="gx-num">{fmtRial(v)}</span> },
  ];

  const costSummary = [
    { dataIndex: "fabric_type", type: "unique" },
    { dataIndex: "color", type: "unique" },
    { dataIndex: "kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "yarn_fi_share", type: "wavg", weightKey: "kg", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "weaving_fi", type: "wavg", weightKey: "kg", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "dyeing_fi", type: "wavg", weightKey: "kg", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "cost_per_kg", type: "wavg", weightKey: "kg", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "total_cost", type: "sum", format: (v) => fmtRial(Math.round(v)) },
  ];

  const stageCard = (def) => {
    const s = stages[def.key] || {};
    const isFreight = def.key === "freight";
    return (
      <Col xs={24} sm={12} xl={6} key={def.key}>
        <Card size="small"
          className="gx-stage-card"
          style={{ opacity: ticked[def.key] ? 1 : 0.45, borderColor: ticked[def.key] ? def.color : undefined }}
          title={<Checkbox checked={ticked[def.key]}
            onChange={(e) => setTicked({ ...ticked, [def.key]: e.target.checked })}>
            <span style={{ color: def.color, fontWeight: 700 }}>{def.label}</span>
          </Checkbox>}>
          <div className="gx-stage-metrics">
            {!isFreight && (
              <div className="gx-stage-metric">
                <div className="gx-stage-metric-label">وزن</div>
                <div className="gx-stage-metric-value">{s.kg == null ? "—" : fmtKg(s.kg)}</div>
              </div>
            )}
            <div className="gx-stage-metric">
              <div className="gx-stage-metric-label">جمع مبلغ</div>
              <div className="gx-stage-metric-value gx-stage-metric-money">
                {s.amount == null ? "—" : fmtRial(s.amount)}
              </div>
            </div>
          </div>
          <div className="gx-stage-metric-foot">
            {isFreight
              ? <>سهم در هر کیلو آماده: {fmtRial(s.fi_per_finished_kg)}
                  {s.included_in_price_amount > 0 && <div>لحاظ‌شده در فی فروشنده (جدا): {fmtRial(s.included_in_price_amount)}</div>}</>
              : <>فی هر کیلو: {fmtRial(s.fi)}</>}
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <div className="gx-cost-per-kg">
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap size="middle" align="center">
          <Segmented options={PERIOD_PRESETS.map((p) => ({ label: p.label, value: p.key }))}
            value={manualFrom || manualTo ? null : preset}
            onChange={(v) => { setPreset(v); setManualFrom(null); setManualTo(null); }} />
          <Space size={4} wrap>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>بازهٔ دستی:</span>
            <JalaliDateInput value={manualFrom} onChange={setManualFrom} />
            <JalaliDateInput value={manualTo} onChange={setManualTo} />
          </Space>
          <Input.Search allowClear placeholder="جست‌وجوی پارچه/رنگ…" style={{ width: 200, maxWidth: "100%" }}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </Space>
        <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>بازه: {range.label}</div>
      </Card>

      {(data?.warnings || []).map((w, i) => (
        <Alert key={i} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
      ))}

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        {STAGE_DEFS.map(stageCard)}
      </Row>

      <Card size="small" title="فی تمام‌شده به تفکیک مدل پارچه (بدون حمل — حمل فقط در سرجمع پایین)"
        style={{ marginBottom: 12 }} styles={{ body: { overflow: "hidden" } }}>
        <div className="gx-cost-table-wrap">
          <GxTable
            className="gx-cost-table"
            layoutId="sc-cost-per-kg"
            dataSource={rows}
            columns={cols}
            size="small"
            rowKey={(r) => `${r.fabric_type || ""}-${r.color || ""}`}
            pagination={rows.length > 15 ? { pageSize: 15 } : false}
            scroll={{ x: TABLE_SCROLL_X }}
            tableLayout="fixed"
            gxSummary={costSummary}
          />
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>
          سهم نخ سراسری تسهیم می‌شود (کیلوی نخ به مدلِ پارچه لینک نیست)؛ تفاوتِ فی مدل‌ها از کارمزدِ بافت و رنگرزیِ خودشان است.
        </div>
      </Card>

      <Card size="small" style={{ borderColor: "#722ed1" }}
        title={`سرجمع (${STAGE_DEFS.filter((d) => ticked[d.key]).map((d) => d.label.split(" ")[0]).join(" + ") || "هیچ"})`}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={8}>
            <div className="gx-stage-metric">
              <div className="gx-stage-metric-label">جمع کل هزینه</div>
              <div className="gx-stage-metric-value gx-stage-metric-money">{fmtRial(pickedTotal)}</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="gx-stage-metric">
              <div className="gx-stage-metric-label">پارچهٔ آماده</div>
              <div className="gx-stage-metric-value">{fmtKg(finishedKg)}</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="gx-stage-metric">
              <div className="gx-stage-metric-label">هر کیلو پارچهٔ آماده ≈</div>
              <div className="gx-stage-metric-value gx-stage-metric-money" style={{ color: "#107C41", fontWeight: 800 }}>
                {pickedFi == null ? "—" : fmtRial(pickedFi)}
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
